import { GraphCanvasEngine } from '@/components/UI/GraphCanvas/GraphCanvasEngine';
import { _isNil, _notNil } from '@/littledash';
import type { AgentSessionApiId, AgentTreeV1 } from '@/model/Agent.model';
import { TerminalEventStatus, TERMINAL_SESSION_STATUSES } from '@/model/Agent.model';
import InVivoError from '@/model/InVivoError';
import { ApiService, ResponseType } from '@/support/ApiService';
import { AgentSessionStreamService } from '@/support/agentStream/AgentSessionStreamService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ACTIVITY_EVENT_TYPES, isTerminalStatus } from './Activity/ActivityRow.utils';
import { AgentActivityEngine } from './Activity/AgentActivityEngine';
import {
  agentTreeGraphConfig,
  mapTreeNodeCreated,
  mapTreeToSnapshot,
  withSessionStatus,
  type AgentSessionMeta,
  type AgentTreeNodeData,
  type ViewReportHandler,
} from './AgentTree.utils';

/** Whether we opened a still-running session (live SSE) or a finished one (replay /events history). */
type SourceMode = 'beganLive' | 'loadedTerminal';

/** Activity-history load state — only meaningful in the loadedTerminal path. */
export type HistoryState = 'idle' | 'loading' | 'error';

export interface AgentSessionStream {
  engine: GraphCanvasEngine<AgentTreeNodeData, AgentSessionMeta>;
  activityEngine: AgentActivityEngine;
  historyState: HistoryState;
}

/**
 * Owns the live machinery for one agent session and wires it together: the tree engine, the activity
 * engine, and (for a still-running session) the SSE transport. All instances are created once via lazy
 * `useState` initializers, so this hook must be called from a component that is mounted once and never
 * conditionally unmounted — the wiring effect is what feeds the engines, and it must survive the
 * `running → terminal` transition.
 *
 * Two mutually-exclusive data sources, selected once from the initial tree's status:
 *  - `beganLive`      — subscribe the transport to the engines + reconciliation, then `connect()`.
 *  - `loadedTerminal` — no stream; replay durable `/events` history into the activity engine once.
 *
 * Returns the engines (for the view to render) plus the activity-history load state.
 */
export const useAgentSessionStream = (
  sessionApiId: AgentSessionApiId,
  initialTree: AgentTreeV1,
  onViewReport: ViewReportHandler
): AgentSessionStream => {
  const [engine] = useState(() => {
    const e = new GraphCanvasEngine<AgentTreeNodeData, AgentSessionMeta>(agentTreeGraphConfig);
    e.setGraph(mapTreeToSnapshot(initialTree, onViewReport));
    return e;
  });
  const [sourceMode] = useState<SourceMode>(() =>
    TERMINAL_SESSION_STATUSES.has(initialTree.session.status) ? 'loadedTerminal' : 'beganLive'
  );
  const [service] = useState<AgentSessionStreamService | null>(() =>
    sourceMode === 'beganLive' ? new AgentSessionStreamService(sessionApiId) : null
  );
  const [activityEngine] = useState(() => {
    const engine = new AgentActivityEngine();
    engine.setStatus(initialTree.session.status);
    return engine;
  });
  const [historyState, setHistoryState] = useState<HistoryState>(sourceMode === 'loadedTerminal' ? 'loading' : 'idle');

  // Reconcile against /tree (authoritative). Fires on a terminal stream event or when the socket
  // drops (status 'reconnecting') — the run may have finished during the gap, so re-fetch the
  // authoritative graph + status and re-hydrate the engine. Single-flight: overlapping triggers
  // coalesce into one in-flight fetch rather than firing parallel refetches. The transport owns
  // reconnection (managed backoff), so this never has to re-open the stream itself.
  // Set once a terminal stream event is seen. Terminal state is monotonic (see AgentActivityEngine),
  // so a later reconcile that fetched a stale pre-terminal `running` snapshot must not downgrade the
  // graph badge back to running — skip re-hydrating in that case.
  const reachedTerminalRef = useRef(false);
  const inFlightReconcileRef = useRef<Promise<void> | null>(null);
  const reconcile = useCallback((): Promise<void> => {
    if (_notNil(inFlightReconcileRef.current)) {
      return inFlightReconcileRef.current;
    }
    const run = (async (): Promise<void> => {
      const response = await ApiService.call({
        endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}/tree',
        path: { sessionApiId },
        options: { onError: { throw: false, toast: false, capture: true, slug: 'agent-session-tree-reconcile' } },
      });
      if (response.type !== ResponseType.Success) {
        return; // best-effort re-sync; a failed reconcile leaves the last-known graph in place
      }
      if (reachedTerminalRef.current && !isTerminalStatus(response.body.session.status)) {
        return; // stale/coalesced snapshot fetched before the run finished — don't regress terminal state
      }
      // `setGraph` replaces the whole node set. The tree is append-only (no node-deleted event), so a
      // node we hold that this /tree response lacks is one the live stream added AFTER the server took
      // this snapshot — preserve it, or a reconcile racing an in-flight `tree_node_created` would drop
      // it. `setGraph` still refreshes badges/layout for the authoritative nodes.
      const snapshot = mapTreeToSnapshot(response.body, onViewReport);
      const authoritativeIds = new Set(snapshot.nodes.map((node) => node.id));
      const streamedOnly = engine.getNodes().filter((node) => !authoritativeIds.has(node.id));
      engine.setGraph({ ...snapshot, nodes: [...snapshot.nodes, ...streamedOnly] });
      activityEngine.setStatus(response.body.session.status); // resolve any still-open rows
    })();
    inFlightReconcileRef.current = run;
    void run.finally(() => {
      inFlightReconcileRef.current = null;
    });
    return run;
  }, [sessionApiId, engine, activityEngine, onViewReport]);

  // Latest-callback refs so the live-wiring effect can depend ONLY on the stable owned instances — a
  // change to reconcile / onViewReport must NOT tear down and reconnect the stream.
  const reconcileRef = useRef(reconcile);
  const onViewReportRef = useRef(onViewReport);
  useEffect(() => {
    reconcileRef.current = reconcile;
    onViewReportRef.current = onViewReport;
  });

  // loadedTerminal only: fetch durable history once, feed the engine. ApiService.call with throw:false
  // RESOLVES an aborted request to { type: Error } (it does NOT reject), so we cannot rely on .catch to
  // ignore StrictMode's abort — gate the apply on `signal.aborted` instead, and capture:false so an
  // expected cancellation is never reported (we report genuine errors ourselves). The AbortController is
  // the single source of truth for "this effect was torn down": it both cancels the in-flight request
  // and (via signal.aborted) blocks a late resolve from applying.
  useEffect(() => {
    if (sourceMode !== 'loadedTerminal') {
      return;
    }
    const controller = new AbortController();
    void ApiService.call({
      endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}/events',
      path: { sessionApiId },
      signal: controller.signal,
      options: { onError: { throw: false, toast: false, capture: false, slug: 'agent-session-events-load' } },
    }).then((response) => {
      if (controller.signal.aborted) {
        return; // stale/aborted — never apply, never error, never report
      }
      if (response.type === ResponseType.Success) {
        // Envelope: { id, redis_stream_id, event }; keep the envelope id for stable row keys.
        activityEngine.ingestAll(response.body.map((entry) => ({ ...entry.event, id: entry.id })));
        setHistoryState('idle');
        return;
      }
      ExceptionHandler.captureException(
        new InVivoError('Failed to load agent activity history', {
          cause: response.error instanceof Error ? response.error : new Error(String(response.error)),
          slug: 'agent-session-events-load',
          level: 'warning',
        })
      );
      setHistoryState('error');
    });
    return () => controller.abort();
  }, [sourceMode, sessionApiId, activityEngine]);

  // Live wiring (beganLive) — subscribe the service to the engine + activity engine + reconciliation in
  // ONE effect, then connect(). One AbortController drives all cleanup. Deps are ONLY the stable owned
  // instances; reconcile/onViewReport are read via refs, so a callback change never reconnects.
  // Subscribe-before-connect is guaranteed here (synchronous).
  const reconciledForDropRef = useRef(false); // one reconcile per drop episode
  useEffect(() => {
    if (_isNil(service)) {
      return; // loadedTerminal — no live stream
    }
    const controller = new AbortController();
    const { signal } = controller;
    service.subscribe(
      'tree_node_created',
      (event) => {
        const meta = engine.getMeta();
        if (_notNil(meta)) {
          engine.upsertNodes([mapTreeNodeCreated(event, meta, onViewReportRef.current)]);
        }
      },
      { signal }
    );
    ACTIVITY_EVENT_TYPES.forEach((type) =>
      service.subscribe(type, (event) => activityEngine.ingest(event), { signal })
    );
    // Resolve terminal state directly from the stream event — a terminal event type IS the terminal
    // session status. This must NOT depend on the reconcile succeeding: a failed or coalesced (stale,
    // still 'running') /tree fetch would otherwise leave open tool rows stuck spinning forever.
    // Reconcile still runs after, to re-sync the authoritative graph. `terminal` is typed as the
    // intersection of stream-event-type and session-status, so it satisfies both subscribe() and
    // setStatus() without a cast.
    Object.values(TerminalEventStatus).forEach((terminal) =>
      service.subscribe(
        terminal,
        () => {
          reachedTerminalRef.current = true;
          activityEngine.setStatus(terminal);
          // Settle the graph badges (root spinner → tick/X) directly from the terminal event too — the
          // badge is baked into node data, so re-map it here rather than waiting on reconcile, which can
          // fail or fetch a stale 'running' snapshot and leave the badge spinning.
          const meta = engine.getMeta();
          if (_notNil(meta)) {
            engine.setGraph({
              meta: { ...meta, status: terminal },
              nodes: withSessionStatus(engine.getNodes(), terminal),
            });
          }
          void reconcileRef.current();
        },
        { signal }
      )
    );
    // A dropped connection (status 'reconnecting') while the run may still be live → reconcile via
    // /tree once, in case the run finished during the gap. Fires once per drop, re-armed on re-open.
    service.subscribeStatus(
      () => {
        const status = service.getConnectionStatus();
        if (status === 'reconnecting' && !reconciledForDropRef.current) {
          reconciledForDropRef.current = true;
          void reconcileRef.current();
        } else if (status === 'open') {
          reconciledForDropRef.current = false;
        }
      },
      { signal }
    );
    service.connect(); // AFTER subscribing — no event missed
    return () => {
      controller.abort(); // removes every listener above
      service.disconnect();
    };
  }, [service, engine, activityEngine]);

  return { engine, activityEngine, historyState };
};
